import type { EditorElement, GroupData } from "./types";

/**
 * Flatten every `group` element into its children at absolute positions,
 * leaving all other elements untouched. Grouping is a design-time aid only;
 * groups are dissolved before preview/save so no grouped object reaches the
 * question data. Mirrors the `ungroup-element` reducer's position math.
 *
 * Groups cannot be nested (the group-elements reducer rejects grouping a
 * selection that already contains a group), so a single pass is sufficient.
 */
export const flattenGroups = (elements: EditorElement[]): EditorElement[] =>
  elements.flatMap((element) => {
    if (element.type !== "group") {
      return [element];
    }
    const children = (element.data as unknown as GroupData).children || [];
    return children.map((child) => ({
      ...child,
      position: {
        x: element.position.x + child.position.x,
        y: element.position.y + child.position.y,
      },
    }));
  });

/** True when at least one group element is present. */
export const hasGroups = (elements: EditorElement[]): boolean =>
  elements.some((element) => element.type === "group");
